import { supabase } from "@/lib/supabase";
import type {
  AcceptInvite,
  InviteDetailsItem,
  InvitesList,
  WorkspaceRole,
} from "@shared/types/supabase.types";
import { type DomainInviteReport, parseDomainInviteReport } from "../domain-invite-capacity";
import { InviteFullError, InviteRpcError } from "./invite-errors";

export { InviteFullError, InviteRpcError };

export async function listContainerInvites(containerId: string): Promise<InvitesList> {
  const { data, error } = await supabase.rpc("get_container_invites", {
    p_container_id: containerId,
  });

  if (error) {
    throw new Error(`Failed to fetch invites: ${error.message}`);
  }

  return data;
}

export async function deleteWorkspaceInvite(inviteId: string): Promise<void> {
  const { error } = await supabase.rpc("delete_workspace_invite", {
    p_invite_id: inviteId,
  });

  if (error) {
    throw new Error(`Failed to delete workspace invite: ${error.message}`);
  }
}

export async function getInviteDetails(inviteCode: string): Promise<InviteDetailsItem[]> {
  const { data, error } = await supabase.rpc("get_invite_details", {
    p_invite_code: inviteCode,
  });

  if (error) {
    throw new Error(`Failed to load invite details: ${error.message}`);
  }

  return data;
}

export async function acceptInvite(inviteCode: string): Promise<AcceptInvite> {
  const { data, error } = await supabase.rpc("accept_invite", {
    p_invite_code: inviteCode,
  });

  if (error) {
    throw new InviteRpcError("Failed to accept workspace invite", error);
  }

  if (data.length === 0) {
    throw new Error("Workspace invite acceptance returned no data");
  }

  return data;
}

export type UpdateDomainInviteInput = {
  inviteId: string;
  containerId: string;
  /** 0 = unlimited. */
  multipleUse: number;
  role: WorkspaceRole;
};

/**
 * CR-185 D9: edit a domain invite's maximum (shared by every container the
 * invite covers) and its role on `containerId` (future joiners only).
 */
export async function updateDomainInvite({
  inviteId,
  containerId,
  multipleUse,
  role,
}: UpdateDomainInviteInput): Promise<void> {
  const { error } = await supabase.rpc("update_domain_invite", {
    p_invite_id: inviteId,
    p_container_id: containerId,
    p_multiple_use: multipleUse,
    p_role: role,
  });

  if (error) {
    throw new InviteRpcError("Failed to update domain invite", error);
  }
}

/**
 * CR-185 D5/S5: tell the owners that a domain invite turned someone away. The
 * refusal itself rolls back, so the client reports it; the RPC re-validates
 * that the invite is full, skips a caller who already holds every row it
 * grants (answering `already_member`) and deduplicates per owner and hour.
 * Best effort: never throws; null when the report failed.
 */
export async function reportDomainInviteFull(
  inviteCode: string,
): Promise<DomainInviteReport | null> {
  try {
    const { data, error } = await supabase.rpc("report_domain_invite_full", {
      p_invite_code: inviteCode,
    });
    if (error) {
      console.warn("domain-invite: report_domain_invite_full failed", error.message);
      return null;
    }
    return parseDomainInviteReport(data);
  } catch (error) {
    console.warn(
      "domain-invite: report_domain_invite_full failed",
      error instanceof Error ? error.message : error,
    );
    return null;
  }
}

/**
 * CR-211: an existing account redeems a workspace-creation sign-up invite and
 * gets the right to create one workspace. 'already_allowed' = the account
 * already holds it (the invite stays untouched). Refusals keep their Postgres error code
 * (see classifyWorkspaceCreationRedeemError).
 */
export async function redeemWorkspaceCreationInvite(
  inviteCode: string,
): Promise<"granted" | "already_allowed"> {
  const { data, error } = await supabase.rpc("redeem_workspace_creation_invite", {
    p_invite_code: inviteCode,
  });

  if (error) {
    throw new InviteRpcError("Failed to redeem workspace creation invite", error);
  }

  return data;
}

/** The invite row behind a code (anon-readable), or null when there is none. */
export async function getInviteByCode(inviteCode: string) {
  const { data, error } = await supabase.rpc("get_invite_by_code", {
    p_invite_code: inviteCode,
  });

  if (error) {
    throw new Error(`Failed to load invite: ${error.message}`);
  }

  return data?.id ? data : null;
}
